import { validDNI, validNIE, validCIF } from 'spain-id';

/**
 * Clase para validar documentos de identidad españoles (DNI, NIE, CIF)
 * Utiliza la librería spain-id para validaciones precisas
 */
export class DniValidator {
    constructor() {
        this.documentTypes = {
            DNI: 'dni',
            NIE: 'nie', 
            CIF: 'cif'
        };
    }

    /**
     * Valida un DNI o NIE
     * @param {string} value - El valor a validar
     * @returns {boolean} - True si es válido
     */
    isValidNIFOrNIE(value) {
        if (!value || typeof value !== 'string') {
            return false;
        }

        const cleanValue = this.normalizeDocument(value);
        
        // Verificar formato básico
        if (!this.hasValidNIFNIEFormat(cleanValue)) {
            return false;
        }

        // Usar spain-id para validación matemática (requiere texto sin guiones)
        return validDNI(cleanValue) || validNIE(cleanValue);
    }

    /**
     * Valida un CIF
     * @param {string} value - El valor a validar
     * @returns {boolean} - True si es válido
     */
    isValidCIF(value) {
        if (!value || typeof value !== 'string') {
            return false;
        }

        const cleanValue = this.normalizeDocument(value);
        
        // Verificar formato básico
        if (!this.hasValidCIFFormat(cleanValue)) {
            return false;
        }

        // Usar spain-id para validación matemática (requiere texto sin guiones)
        return validCIF(cleanValue);
    }

    /**
     * Normaliza un documento eliminando espacios, guiones y convirtiendo a mayúsculas
     * @param {string} value - El valor a normalizar
     * @returns {string} - El valor normalizado
     */
    normalizeDocument(value) {
        if (!value || typeof value !== 'string') {
            return '';
        }
        
        return value.trim().toUpperCase().replace(/[.-\s]/g, '');
    }

    /**
     * Valida cualquier tipo de documento español
     * @param {string} value - El valor a validar
     * @returns {object} - {isValid: boolean, type: string|null, message: string}
     */
    validateSpanishDocument(value, isCompany) {
        if (!value || typeof value !== 'string') {
            return {
                isValid: false,
                type: null,
                message: 'Documento requerido'
            };
        }

        const cleanValue = this.normalizeDocument(value);
        const type = this.detectDocumentType(cleanValue);

        if (!type) {
            return {
                isValid: false,
                type: null,
                message: 'Formato de documento no reconocido'
            };
        }

        let isValid = false;
        let message = '';

        switch (isCompany) {
            // case this.documentTypes.DNI:
            // case this.documentTypes.NIE:
            case false:
                isValid = this.isValidNIFOrNIE(value); // Pasamos el valor original, la función ya normaliza
                message = isValid ? 'DNI/NIE válido' : 'DNI/NIE no válido';
                break;
            // case this.documentTypes.CIF:
            case true:
                isValid = this.isValidCIF(value); // Pasamos el valor original, la función ya normaliza
                message = isValid ? 'CIF válido' : 'CIF no válido';
                break;
        }

        return {
            isValid,
            type,
            message
        };
    }

    /**
     * Detecta el tipo de documento
     * @param {string} value - El valor a analizar
     * @returns {string|null} - 'dni', 'nie', 'cif' o null
     */
    detectDocumentType(value) {
        if (!value || typeof value !== 'string') {
            return null;
        }

        const cleanValue = this.normalizeDocument(value);

        // NIE: Empieza con X, Y o Z
        if (/^[XYZ]/.test(cleanValue)) {
            return this.documentTypes.NIE;
        }

        // CIF: Empieza con letra (excepto X, Y, Z)
        if (/^[ABCDEFGHJNPQRSUVW]/.test(cleanValue)) {
            return this.documentTypes.CIF;
        }

        // DNI: Empieza con número
        if (/^[0-9]/.test(cleanValue)) {
            return this.documentTypes.DNI;
        }

        return null;
    }

    /**
     * Formatea un documento añadiendo guiones si es necesario
     * @param {string} value - El valor a formatear
     * @returns {string} - El valor formateado
     */
    formatDocument(value) {
        if (!value || typeof value !== 'string') {
            return '';
        }

        const cleanValue = value.trim().toUpperCase().replace(/[-\s]/g, '');
        const type = this.detectDocumentType(cleanValue);

        if (!type) {
            return cleanValue;
        }

        // Formatear según el tipo
        switch (type) {
            case this.documentTypes.DNI:
                // 12345678A -> 12345678-A
                if (cleanValue.length === 9) {
                    return `${cleanValue.slice(0, 8)}-${cleanValue.slice(8)}`;
                }
                break;
            case this.documentTypes.NIE:
                // X1234567A -> X-1234567-A
                if (cleanValue.length === 9) {
                    return `${cleanValue.slice(0, 1)}-${cleanValue.slice(1, 8)}-${cleanValue.slice(8)}`;
                }
                break;
            case this.documentTypes.CIF:
                // A12345674 -> A-12345674
                if (cleanValue.length === 9) {
                    return `${cleanValue.slice(0, 1)}-${cleanValue.slice(1)}`;
                }
                break;
        }

        return cleanValue;
    }

    /**
     * Verifica el formato básico de DNI/NIE (sin validación matemática)
     * @param {string} value - El valor a verificar
     * @returns {boolean} - True si tiene formato válido
     */
    hasValidNIFNIEFormat(value) {
        // DNI: 8 dígitos + 1 letra
        const dniPattern = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/;
        // NIE: X/Y/Z + 7 dígitos + 1 letra
        const niePattern = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/;
        
        return dniPattern.test(value) || niePattern.test(value);
    }

    /**
     * Verifica el formato básico de CIF (sin validación matemática)
     * @param {string} value - El valor a verificar
     * @returns {boolean} - True si tiene formato válido
     */
    hasValidCIFFormat(value) {
        // CIF: 1 letra + 7 dígitos + 1 dígito/letra
        const cifPattern = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/;
        return cifPattern.test(value);
    }


    /**
     * Valida un campo de documento y añade feedback visual
     * @param {HTMLElement} value - El campo a validar
     * @param {boolean} isCompany - Si es una empresa (para CIF) o particular (DNI/NIE)
     * @returns {boolean} - True si es válido
     */
    validateDocumentField(value, isCompany = false) {
        if (!value) {
            return {
                isValid: false,
                type: null,
                message: 'Este campo es obligatorio'
            };
        }

        const result = this.validateSpanishDocument(value, isCompany);
        
        // Verificar si el tipo de documento coincide con el tipo de cliente
        if (result.isValid) {
            if (isCompany && result.type !== this.documentTypes.CIF) {

                result.isValid = false;
                result.message = 'Las empresas deben usar CIF';

            } else if (!isCompany && result.type === this.documentTypes.CIF) {
                
                result.isValid = false;
                result.message = 'Los particulares deben usar DNI o NIE';

            }
        }

        return result;
    }
}

// Exportar una instancia por defecto
export default new DniValidator();